import { getDaysInMonth } from "@/utils/dayUtill";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import type { AssignmentStartInfo } from "./types";

/**
 * `AssignmentGroupBar`에 올리지 않는 사유.
 * 인바운드·assignments 메타에 프리셉터/프리셉티 문자열이 섞여 들어와도 기간 바는 숨긴다.
 */
export function isAssignmentBarSuppressedReason(reason: unknown): boolean {
  if (reason == null) return false;
  const r = String(reason).trim().toLowerCase();
  if (!r) return false;
  if (r === "outsider" || r === "outbound") return false;
  return (
    r.includes("프리셉티") ||
    r.includes("프리셉터") ||
    r.includes("preceptee") ||
    r.includes("preceptor")
  );
}

type Ymd = { y: number; m: number; d: number };

function parseYmd(iso: string | null | undefined): Ymd | null {
  if (!iso) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

function cmpYmd(a: Ymd, b: Ymd): number {
  if (a.y !== b.y) return a.y - b.y;
  if (a.m !== b.m) return a.m - b.m;
  return a.d - b.d;
}

/**
 * 특정 nurse 의 inbound 목록을 당월 기준 렌더 메타 배열로 변환.
 *
 * - timezone 의존 없이 ISO 날짜 문자열을 수치로만 비교
 * - 1건 = 1 AssignmentStartInfo (시작 셀에서만 렌더)
 * - startDay: 당월 그리드 시작일 (clip 후 1..daysInMonth)
 * - rangeLength: endDay - startDay + 1
 * - endDate === null → 당월 말일까지 (clippedEnd=true)
 */
export function computeAssignmentStarts(
  items: InboundItem[],
  year: number,
  month: number
): AssignmentStartInfo[] {
  if (!items || items.length === 0) return [];

  const y = Math.trunc(Number(year));
  const mo = Math.trunc(Number(month));
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12 || y < 1) return [];

  const dim = getDaysInMonth(y, mo);
  const viewFirst: Ymd = { y, m: mo, d: 1 };
  const viewLast: Ymd = { y, m: mo, d: dim };

  const result: AssignmentStartInfo[] = [];

  for (const item of items) {
    const start = parseYmd(item.startDate);
    if (!start) continue;

    const end: Ymd | null = item.endDate ? parseYmd(item.endDate) : null;
    if (item.endDate && !end) continue;

    // 당월과 겹치지 않으면 skip
    if (cmpYmd(start, viewLast) > 0) continue;
    if (end && cmpYmd(end, viewFirst) < 0) continue;

    const clippedStart = cmpYmd(start, viewFirst) < 0;
    const clippedEnd = end == null || cmpYmd(end, viewLast) > 0;

    /** 뷰 월과 startDate 의 YYYY-MM 이 같으면 DD 는 문자열에서 직접 사용 (분기 실패로 startDay=1 로 떨어지는 것 방지) */
    const viewYmKey = `${y}-${String(mo).padStart(2, "0")}`;
    const startYmFromIso =
      item.startDate.length >= 7 ? item.startDate.slice(0, 7) : "";

    let startDay: number;
    if (clippedStart) {
      startDay = 1;
    } else if (startYmFromIso === viewYmKey) {
      const dd = Number(item.startDate.slice(8, 10));
      startDay = Number.isFinite(dd)
        ? Math.min(Math.max(dd, 1), dim)
        : Math.min(Math.max(start.d, 1), dim);
    } else if (start.y === y && start.m === mo) {
      startDay = Math.min(Math.max(start.d, 1), dim);
    } else {
      startDay = 1;
    }

    const endDay =
      end == null || clippedEnd
        ? dim
        : end.y === y && end.m === mo
          ? Math.min(Math.max(end.d, 1), dim)
          : dim;

    if (startDay > endDay) continue;

    if (isAssignmentBarSuppressedReason(item.reason)) continue;

    result.push({
      reason: item.reason,
      startDay,
      rangeLength: endDay - startDay + 1,
      targetGroupId:
        item.target_group_id ?? item.targetGroupId ?? undefined,
      targetGroupName:
        item.target_group_name ?? item.targetGroupName ?? undefined,
      clippedStart,
      clippedEnd,
    });
  }

  return result;
}

/**
 * prev-tail 좌표계를 포함한 확장 버전.
 *
 * tailDays: 전월 tail 일자 배열 (예: [26,27,28,29,30,31], 정렬 필요).
 *   - 정렬된 tailDays[i] ↔ 가상 좌표 (i - tailDays.length), 즉 -tailLen .. -1
 *   - 현재월 1 .. dim 과 이어짐 (0은 없음)
 *
 * rangeLength = endDay - startDay + 1 공식은 음수→양수 범위도 그대로 적용.
 *   예) startDay=-3, endDay=10 → rangeLength=14
 *
 * tailDays 미전달/빈 배열 → 기존 computeAssignmentStarts 와 동일 동작.
 */
export function computeAssignmentStartsExtended(
  items: InboundItem[],
  year: number,
  month: number,
  tailDays?: number[]
): AssignmentStartInfo[] {
  if (!tailDays || tailDays.length === 0) {
    return computeAssignmentStarts(items, year, month);
  }

  if (!items || items.length === 0) return [];

  const y = Math.trunc(Number(year));
  const mo = Math.trunc(Number(month));
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12 || y < 1) return [];

  const dim = getDaysInMonth(y, mo);

  // tail 월 (전월) 계산
  const prevMo = mo === 1 ? 12 : mo - 1;
  const prevY = mo === 1 ? y - 1 : y;

  // tail 좌표 매핑: tailDays[i] → (i - tailDays.length)
  const sortedTail = [...tailDays].sort((a, b) => a - b);
  const tailLen = sortedTail.length;

  // tail 일자 → 가상 좌표 맵
  const tailDayToCoord = new Map<number, number>();
  for (let i = 0; i < sortedTail.length; i++) {
    tailDayToCoord.set(sortedTail[i], i - tailLen);
  }
  const tailFirstCoord = -tailLen; // 가장 이른 tail 좌표
  const tailLastCoord = -1;        // 가장 늦은 tail 좌표

  const viewFirst: Ymd = { y, m: mo, d: 1 };
  const viewLast: Ymd = { y, m: mo, d: dim };
  const tailFirstYmd: Ymd = { y: prevY, m: prevMo, d: sortedTail[0] };

  const result: AssignmentStartInfo[] = [];

  for (const item of items) {
    const start = parseYmd(item.startDate);
    if (!start) continue;

    const end: Ymd | null = item.endDate ? parseYmd(item.endDate) : null;
    if (item.endDate && !end) continue;

    // tail 첫 날보다 뒤에 끝나거나 현재월보다 앞에 시작 → 범위 내
    if (cmpYmd(start, viewLast) > 0) continue;
    if (end && cmpYmd(end, tailFirstYmd) < 0) continue;

    // --- startDay 계산 ---
    let startDay: number;
    const clippedStart =
      cmpYmd(start, tailFirstYmd) < 0 ||
      (start.y === prevY && start.m === prevMo && !tailDayToCoord.has(start.d) && start.d < sortedTail[0]);

    if (clippedStart) {
      startDay = tailFirstCoord;
    } else if (start.y === prevY && start.m === prevMo) {
      // prev tail 영역
      const coord = tailDayToCoord.get(start.d);
      if (coord !== undefined) {
        startDay = coord;
      } else {
        // tail_days 에 없는 전월 일자 (비연속) → 가장 가까운 tail 좌표로 clip
        const nearest = sortedTail.findIndex((d) => d >= start.d);
        startDay = nearest === -1 ? tailLastCoord : nearest - tailLen;
      }
    } else if (start.y === y && start.m === mo) {
      // 현재월 영역
      const viewYmKey = `${y}-${String(mo).padStart(2, "0")}`;
      const startYmFromIso = item.startDate.length >= 7 ? item.startDate.slice(0, 7) : "";
      if (startYmFromIso === viewYmKey) {
        const dd = Number(item.startDate.slice(8, 10));
        startDay = Number.isFinite(dd) ? Math.min(Math.max(dd, 1), dim) : Math.min(Math.max(start.d, 1), dim);
      } else {
        startDay = Math.min(Math.max(start.d, 1), dim);
      }
    } else {
      startDay = tailFirstCoord;
    }

    // --- endDay 계산 ---
    let endDay: number;
    const clippedEnd = end == null || cmpYmd(end, viewLast) > 0;

    if (end == null || clippedEnd) {
      endDay = dim;
    } else if (end.y === y && end.m === mo) {
      endDay = Math.min(Math.max(end.d, 1), dim);
    } else if (end.y === prevY && end.m === prevMo) {
      // 전월에서 끝나는 경우
      const coord = tailDayToCoord.get(end.d);
      if (coord !== undefined) {
        endDay = coord;
      } else {
        const nearest = [...sortedTail].reverse().find((d) => d <= end!.d);
        endDay = nearest !== undefined ? tailDayToCoord.get(nearest)! : tailLastCoord;
      }
    } else {
      endDay = dim;
    }

    if (startDay > endDay) continue;

    if (isAssignmentBarSuppressedReason(item.reason)) continue;

    // rangeLength: 음수→양수 범위 포함 (0 좌표 건너뜀)
    const rangeLength =
      startDay < 0 && endDay >= 1
        ? (-startDay) + endDay  // tail칸 수 + 현재월 칸 수 (0 없음)
        : endDay - startDay + 1;

    const dedupKey = `${item.reason}|${item.target_group_id ?? item.targetGroupId ?? ""}|${item.startDate}|${item.endDate ?? ""}`;

    result.push({
      reason: item.reason,
      startDay,
      rangeLength,
      targetGroupId: item.target_group_id ?? item.targetGroupId ?? undefined,
      targetGroupName: item.target_group_name ?? item.targetGroupName ?? undefined,
      clippedStart,
      clippedEnd,
      dedupKey,
    });
  }

  return result;
}
